"use client";

import { useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import ScrollTrigger from "gsap/ScrollTrigger";
import { ArrowDown } from "iconoir-react";
import { TourHighlights } from "./TourHighlights";
import { BookTourButton } from "./BookTourButton";

gsap.registerPlugin(ScrollTrigger);

export const LandingPage = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const row1Ref = useRef<HTMLDivElement>(null);
  const row2Ref = useRef<HTMLDivElement>(null);
  const row3Ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!heroRef.current) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

        tl.fromTo(
          ".hero-symbol",
          { opacity: 0, scale: 0.6 },
          { opacity: 1, scale: 1, duration: 0.8 },
        )
          .fromTo(
            ".hero-title",
            { opacity: 0, y: 40 },
            { opacity: 1, y: 0, duration: 0.9 },
            "-=0.3",
          )
          .fromTo(
            ".hero-subtitle",
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.7 },
            "-=0.4",
          )
          .fromTo(
            ".hero-cta",
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6 },
            "-=0.35",
          )
          .fromTo(
            ".hero-scroll",
            { opacity: 0 },
            { opacity: 1, duration: 0.5 },
            "-=0.2",
          );
      });

      mm.add("(prefers-reduced-motion: reduce)", () => {
        gsap.fromTo(
          [".hero-symbol", ".hero-title", ".hero-subtitle", ".hero-cta", ".hero-scroll"],
          { opacity: 0 },
          { opacity: 1, duration: 0.6, stagger: 0.15, ease: "power1.out" },
        );
      });

      const animateRow = (rowEl: HTMLDivElement | null, reverse: boolean) => {
        if (!rowEl) return;

        mm.add("(prefers-reduced-motion: no-preference)", () => {
          const imageEl = rowEl.querySelector(".row-image-wrapper");
          const textChildren = rowEl.querySelectorAll(".row-text-child");
          const fromX = reverse ? 60 : -60;

          gsap.fromTo(
            imageEl,
            { opacity: 0, scale: 1.05 },
            {
              opacity: 1,
              scale: 1,
              duration: 1,
              ease: "power2.out",
              scrollTrigger: { trigger: rowEl, start: "top 75%", once: true },
            },
          );

          gsap.fromTo(
            textChildren,
            { opacity: 0, x: fromX },
            {
              opacity: 1,
              x: 0,
              duration: 0.7,
              stagger: 0.12,
              ease: "power2.out",
              scrollTrigger: { trigger: rowEl, start: "top 75%", once: true },
            },
          );
        });

        mm.add("(prefers-reduced-motion: reduce)", () => {
          const imageEl = rowEl.querySelector(".row-image-wrapper");
          const textChildren = rowEl.querySelectorAll(".row-text-child");

          gsap.fromTo(
            [imageEl, ...Array.from(textChildren)],
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.8,
              stagger: 0.1,
              ease: "power1.out",
              scrollTrigger: { trigger: rowEl, start: "top 75%", once: true },
            },
          );
        });
      };

      animateRow(row1Ref.current, false);
      animateRow(row2Ref.current, true);
      animateRow(row3Ref.current, false);
    },
    { scope: heroRef },
  );

  return (
    <div className="landing-page grid grid-cols-12 md:gap-3xl px-md sm:px-2xl md:px-3xl 2xl:px-[8rem] pt-32 md:pt-64 pb-12">
      <div className="title-container col-span-12 md:col-span-8 md:col-start-3 mb-3xl">
        <div className="w-full flex flex-col gap-4 items-center justify-center">
          <div className="hero-symbol">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 312.9444 295.3079"
              className="w-2xl h-2xl fill-primary-light"
            >
              <path d="M137.756 294.8268c-3.4454-.893-3.9554-3.2256-3.9554-18.0905 0-14.5224.0488-14.3782-4.8662-14.3782-3.0603 0-4.9624 1.6287-9.5496 8.1772-16.1537 23.06-35.8857 29.0544-70.673 21.47-19.1558-4.1765.9542-26.7625 21.5163-24.1655 9.877 1.2475 13.749-.8637 16.8666-9.1965 1.982-5.2972 1.9335-5.3638-6.294-8.6562C-13.1 212.411-28.2684 117.571 51.7167 68.1415c3.0706-1.8975 13.2706-7.6774 22.6667-12.8441 15.4935-8.5195 17.0675-9.5435 16.908-11-.5023-4.591-7.7996-10.6004-14.4132-11.8698-1.876-.36-5.961-1.1471-9.0777-1.749-10.9134-2.1078-13.1256-3.696-17.8818-12.838-2.7983-5.3786-2.7983-5.3786.9634-7.1008 27.8144-12.7338 52.564-3.6295 64.792 23.8343 6.9721 15.6589 18.7931 8.146 18.7931-11.944 0-24.2667-1.5901-22.6054 21.6364-22.6054 22.883 0 21.1864-1.539 21.6806 19.6667.468 20.0735 1.033 21.2385 9.1125 18.7844 4.486-1.3626 4.7431-1.5592 7.7514-5.9302C211.048 8.7171 227.29 2.4402 260.0658 7.2641c25.9886 3.825 12.4718 27.3891-14.9542 26.0699-11.7734-.5663-12.148-.3628-16.9209 9.1929-3.079 6.1645-3.079 6.1645 10.4321 12.8312 103.7498 51.1921 97.8165 155.6315-11.1555 196.3609-8.9078 3.3294-8.8637 3.2902-7.0372 6.2455 3.6398 5.8894 8.447 8.3485 19.4675 9.959 11.983 1.751 20.1974 7.4962 22.5758 15.7893 1.746 6.0878-3.526 7.81-27.6728 9.0393-18.5288.9434-28.8645-5.3044-37.9396-22.9339-3.5285-6.8546-7.1154-8.6686-14.3937-7.279-4.3333.8273-4.3333.8273-4.6667 14.9763-.4541 19.2792 1.3905 17.7136-20.9569 17.7868-9.371.0307-17.9605-.1833-19.0877-.4755m-3.2887-140.8985c0-99.4725 1.853-89.9068-16.3912-84.6138-97.266 28.2185-100.2637 136.383-4.6088 166.2903 9.2603 2.8953 19.6786 5.9652 20.5 6.0407.275.0252.5-39.4475.5-87.7172m54.5085 85.3764c86.6473-19.4334 111.5891-107.9918 43.2831-153.6809-12.991-8.6895-43.7782-20.599-53.2505-20.599-.2976 0-.541 39.6-.541 88 0 88 0 88 1.4196 88 .7809 0 4.8708-.774 9.0888-1.7201" />
            </svg>
          </div>

          <h1 className="hero-title text-5xl sm:5xl md:text-6xl lg:8xl font-reem-kufi capitalize tracking-tight text-text-secondary leading-none">
            Discover Seattle
          </h1>

          <p className="hero-subtitle text-xs md:text-sm font-open-sans uppercase tracking-[0.25em] text-sand-700">
            Central District Heritage Tours
          </p>

          {/* Wrapped so the centring comes from this flex column rather than
              fighting the button's own self-start. */}
          <div className="hero-cta mt-lg md:mt-xl">
            <BookTourButton variant="dark" />
          </div>
        </div>
      </div>
      <div className="hero-image-container col-span-12">
        <Image
          src="/photos/liberty_bank_building.jpg"
          alt="Tour group entering the Liberty Bank Building property"
          width={2048}
          height={1365}
          className="hidden sm:block w-full rounded-4xl"
        />

        <Image
          src="/photos/liberty_bank_building_portrait.jpg"
          alt="Tour group entering the Liberty Bank Building property"
          width={1638}
          height={2048}
          className="block sm:hidden w-full rounded-4xl"
        />
      </div>

      {/* Row — Tour Highlights */}
      <div className="tour-highlights-container col-span-12 grid-cols-subgrid">
        <div className="flex flex-col min-h-[60vh] w-full px-2 md:px-8 lg:px-16 py-12 md:py-20 bg-sand-100">
          {/* <h2 className="row-text-child text-3xl md:text-5xl font-reem-kufi font-light leading-tight mb-6 text-brown-800">
            Tour Highlights
          </h2>
          <p
            className="row-text-child font-open-sans text-base md:text-lg leading-relaxed max-w-[50ch] text-brown-600 mb-8"
            style={{ fontFamily: "var(--font-open-sans)" }}
          >
            Explore what makes the Africatown Black Seattle Tour unforgettable —
            from food and landmarks to the history, community, and legacy of
            the Central District.
          </p> */}
          <TourHighlights />
        </div>
      </div>

      {/* Content Rows */}

      {/* Row — Hook */}
      <div className="hook-container col-span-12 grid-cols-subgrid">
        <div
          ref={row1Ref}
          className="flex flex-col md:flex-row min-h-[60vh] w-full"
        >
          <div className="w-full md:w-1/2 flex flex-col justify-center px-2 md:px-8 lg:px-16 py-12 md:py-20 bg-sand-100">
            {/* <p
              className="row-text-child font-open-sans text-sm uppercase tracking-[0.2em] mb-4 text-coral-500"
              style={{ fontFamily: "var(--font-open-sans)" }}
            >
              Legacy
            </p> */}
            <h2 className="row-text-child text-3xl md:text-5xl font-reem-kufi font-light leading-tight mb-6 text-brown-800">
              Discover the legends that called Seattle home
            </h2>
            <p
              className="row-text-child font-open-sans text-base md:text-lg leading-relaxed max-w-[50ch] text-brown-600"
              style={{ fontFamily: "var(--font-open-sans)" }}
            >
              What do Ray Charles, Jimi Hendrix, Quincy Jones, Macklemore, Sir
              Mix-A-Lot, and Kenny G have in common?
              <br />
              <br />
              They were all shaped by the Central District of Seattle.
              <br />
              <br />
              The Africatown Black Seattle Tour is an immersive journey through
              one of America's most remarkable — and most overlooked — facets of
              African American history and culture.
            </p>
          </div>
          <div className="row-image-wrapper relative w-full grid-cols-1 overflow-hidden">
            <Image
              src="/photos/central_district.jpg"
              alt="Community members playing double dutch in the street."
              width={2048}
              height={1365}
              sizes="(max-width: 768px) 100vw, 50vw"
              className="w-full h-auto rounded-4xl"
            />
          </div>
        </div>
      </div>

      {/* Row — Community */}
      <div className="community-container col-span-12 md:col-span-10 md:col-start-2 grid-cols-subgrid">
        <div
          ref={row1Ref}
          className="flex flex-col md:flex-row-reverse min-h-[60vh] w-full"
        >
          <div className="w-full md:w-1/2 flex flex-col justify-center px-2 md:px-8 lg:px-16 py-12 md:py-20 bg-sand-100">
            {/* <p
              className="row-text-child font-open-sans text-xs uppercase tracking-[0.2em] mb-4 text-coral-500"
              style={{ fontFamily: "var(--font-open-sans)" }}
            >
              Community
            </p> */}
            <h2
              className="row-text-child text-3xl md:text-5xl leading-tight mb-6 text-brown-800"
              style={{ fontFamily: "var(--font-reem-kufi)" }}
            >
              A Living Community
            </h2>
            <p
              className="row-text-child font-open-sans text-base md:text-lg leading-relaxed max-w-[50ch] text-brown-600"
              style={{ fontFamily: "var(--font-open-sans)" }}
            >
              Experience a living, breathing story told through the
              neighborhood, landmarks, and legacies that still pulse through
              Seattle today. <br />
              <br />
              AfricaTown Central District is a living testament to resilience,
              culture, and collective pride.
            </p>
          </div>
          <div className="row-image-wrapper relative w-full grid-cols-1 min-h-[40vh] md:min-h-[60vh] overflow-hidden">
            <Image
              src="/photos/events_umojafest_4.jpg"
              alt="Community members playing double dutch in the street."
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover object-center rounded-4xl"
            />
          </div>
        </div>
      </div>

      {/* Row — Call to Action */}
      <div className="col-span-12 grid-cols-subgrid relative">
        <div
          ref={row3Ref}
          className="flex flex-col md:flex-row min-h-[70vh] w-full relative"
        >
          {/* Left Column: Text Content (Apportioned 25% on desktop) */}
          <div className="w-full md:w-1/3 flex flex-col justify-center px-4 md:px-6 lg:px-8 py-12 md:py-20 bg-sand-100 relative">
            <h2 className="row-text-child font-['Reem_Kufi'] text-3xl md:text-4xl leading-tight mb-6 text-brown-800">
              Join the Celebration
            </h2>
            <p className="row-text-child font-['Open_Sans'] text-sm md:text-base leading-relaxed text-brown-600">
              This tour reveals a side of Seattle most people never get to see.{" "}
              <br />
              <br />
              Whether you're a local or just visiting, you'll walk away with an
              experience to remember for years to come.
            </p>

            <BookTourButton variant="dark" className="row-text-child mt-8" />
          </div>

          {/* Right Column: Photo Playground (Apportioned 75% on desktop) */}
          <div className="w-full md:w-2/3 relative min-h-[60vh] md:min-h-full py-0">
            {/* Photo 1: Landscape (Base Layer / Backgrounded) */}
            <div className="relative sm:absolute sm:left-[5%] sm:top-[10%] w-full sm:w-[65%] aspect-[2048/1365] rounded-4xl overflow-hidden will-change-transform transition-transform duration-100 ease-out">
              <Image
                src="/photos/welcoming.jpg"
                alt="Umoja FEST celebration"
                width={2048}
                height={1365}
                sizes="(max-width: 768px) 65vw, 50vw"
                className="w-full h-auto"
              />
            </div>

            {/* Photo 2: Portrait (Foreground Layer - Overlaps and breaks boundaries) */}
            <div className="hidden md:block absolute right-[5%] bottom-[5%] w-[40%] aspect-[1638/2048] rounded-4xl overflow-hidden will-change-transform transition-transform duration-100 ease-out">
              <Image
                src="/photos/smiling_people_portrait.jpg"
                alt="Umoja FEST celebration portrait"
                width={1638}
                height={2048}
                sizes="(max-width: 768px) 40vw, 30vw"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
